import { request } from "../client";

import type {
  UserProfile,
  UpdateProfilePayload,
  EnhancedProgressStats,
  LimitsDashboardResponse,
  UserDataExport,
  UserFeaturesResponse,
} from "../types/user";

export function createUserEndpoints(baseUrl: string): {
  getProfile: (token?: string | null) => Promise<{ user: UserProfile }>;
  updateProfile: (
    payload: UpdateProfilePayload,
    token?: string | null,
  ) => Promise<{ user: UserProfile }>;
  deleteAccount: (token?: string | null) => Promise<{ success: boolean }>;
  exportUserData: (token?: string | null) => Promise<UserDataExport>;
  getSettings: (token?: string | null) => Promise<Record<string, unknown>>;
  updateSettings: (
    settings: Record<string, unknown>,
    token?: string | null,
  ) => Promise<{ success: boolean }>;
  getEnhancedStats: (token?: string | null) => Promise<EnhancedProgressStats>;
  getLimitsDashboard: (
    token?: string | null,
  ) => Promise<
    | { success?: boolean; data: LimitsDashboardResponse }
    | LimitsDashboardResponse
  >;
  getFeatures: (token?: string | null) => Promise<UserFeaturesResponse>;
  activatePromoCode: (
    code: string,
    token?: string | null,
  ) => Promise<{ success: boolean; message: string }>;
} {
  return {
    /**
     * Получить профиль текущего пользователя
     */
    getProfile(token?: string | null): Promise<{ user: UserProfile }> {
      return request<{ user: UserProfile }>(baseUrl, "/user/profile", {
        method: "GET",
        token,
      });
    },

    /**
     * Обновить профиль пользователя
     */
    updateProfile(
      payload: UpdateProfilePayload,
      token?: string | null,
    ): Promise<{ user: UserProfile }> {
      return request<{ user: UserProfile }>(baseUrl, "/user/profile", {
        method: "PUT",
        body: payload,
        token,
      });
    },

    /**
     * Удалить аккаунт пользователя
     */
    deleteAccount(token?: string | null): Promise<{ success: boolean }> {
      return request<{ success: boolean }>(baseUrl, "/user/account", {
        method: "DELETE",
        token,
      });
    },

    /**
     * Экспорт персональных данных пользователя (GDPR portability)
     */
    exportUserData(token?: string | null): Promise<UserDataExport> {
      return request<UserDataExport>(baseUrl, "/user/data/export", {
        method: "GET",
        token,
      });
    },

    /**
     * Получить настройки пользователя
     */
    getSettings(token?: string | null): Promise<Record<string, unknown>> {
      return request<Record<string, unknown>>(baseUrl, "/user/settings", {
        method: "GET",
        token,
      });
    },

    /**
     * Обновить настройки пользователя
     */
    updateSettings(
      settings: Record<string, unknown>,
      token?: string | null,
    ): Promise<{ success: boolean }> {
      return request<{ success: boolean }>(baseUrl, "/user/settings", {
        method: "PUT",
        body: settings,
        token,
      });
    },

    /**
     * Получить расширенную статистику прогресса пользователя
     * (используется клиентскими приложениями, ранее вызывалось напрямую через axios)
     */
    getEnhancedStats(token?: string | null): Promise<EnhancedProgressStats> {
      return request<EnhancedProgressStats>(baseUrl, "/user/enhanced-stats", {
        method: "GET",
        token,
      });
    },

    /**
     * Получить информацию об ограничениях и текущем использовании (dashboard)
     * Ранее вызывалось напрямую через axios из клиента
     */
    getLimitsDashboard(
      token?: string | null,
    ): Promise<
      | { success?: boolean; data: LimitsDashboardResponse }
      | LimitsDashboardResponse
    > {
      return request<
        | { success?: boolean; data: LimitsDashboardResponse }
        | LimitsDashboardResponse
      >(baseUrl, "/user/limits/dashboard", {
        method: "GET",
        token,
      });
    },

    /**
     * Получить доступные пользователю фичи и usage по лимитам
     */
    getFeatures(token?: string | null): Promise<UserFeaturesResponse> {
      return request<UserFeaturesResponse>(baseUrl, "/user/features", {
        method: "GET",
        token,
      });
    },

    /**
     * Активировать промокод для текущего пользователя
     */
    activatePromoCode(
      code: string,
      token?: string | null,
    ): Promise<{ success: boolean; message: string }> {
      return request<{ success: boolean; message: string }>(
        baseUrl,
        "/user/promo-code",
        {
          method: "POST",
          body: { code },
          token,
        },
      );
    },
  };
}
